// src/services/reportService.js
// NOTE: The /order-returns backend endpoint does NOT support branch filtering.
// All returns data is fetched in full and filtered on the frontend using order.branch._id.
import ApiService from './api'
import AppURLs from '../config/urls'

class ReportService {
  // ─── Date helpers ────────────────────────────────────────────────────────────

  static getTodayDateRange() {
    const today = new Date()
    return {
      startOfToday: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      endOfToday:   new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
      todayDateString: today.toISOString().split('T')[0]
    }
  }

  static isToday(dateString) {
    if (!dateString) return false
    const { todayDateString } = ReportService.getTodayDateRange()
    return new Date(dateString).toISOString().split('T')[0] === todayDateString
  }

  // ─── Branch matching helper ───────────────────────────────────────────────────
  // Checks if a return record belongs to the given branchId.
  // Tries all known paths the branch id can be stored under.
  static returnMatchesBranch(r, branchId) {
    if (!branchId) return true // no filter if branchId unknown
    const candidates = [
      r.order?.branch?._id,
      r.order?.branch,
      r.branch?._id,
      r.branch,
      r.order?.branchId,
      r.branchId
    ]
    return candidates.some(c => c && String(c) === String(branchId))
  }

  // ─── Discount extraction ──────────────────────────────────────────────────────
  static extractDiscount(order) {
    const items = order.orderItems || order.items || []
    const itemDiscount = items.reduce((sum, item) =>
      sum + parseFloat(item.promotionEngineResult?.totals?.totalDiscount || 0), 0)
    if (itemDiscount > 0) return itemDiscount
    return parseFloat(order.orderPromotionEngineResult?.orderDetails?.totalDiscount || order.discount || 0)
  }

  // ─── Sales Report ─────────────────────────────────────────────────────────────
  // Orders: filtered server-side by branch via API param.
  // Returns: fetched all, then filtered client-side by branch.
  static async getSalesReport(branchId, date = null) {
    try {
      const { todayDateString } = ReportService.getTodayDateRange()
      const targetDate = date || todayDateString

      // Orders — the orders API supports branch param
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, {
        branch: branchId,
        startDate: targetDate,
        endDate: targetDate,
        dateFrom: targetDate,
        dateTo: targetDate,
        orderStatus: 'completed',
        paymentStatus: 'paid'
      })
      const data = response

      if (data.success && data.data?.orders) {
        let orders = data.data.orders

        // Double-check: strict today + status filter (API sometimes returns wider range)
        orders = orders.filter(order => {
          const dateStr = new Date(order.orderDate || order.createdAt).toISOString().split('T')[0]
          return dateStr === todayDateString &&
            order.orderStatus === 'completed' &&
            order.paymentStatus === 'paid'
        })

        const salesRows = orders.map(order => ({
          orderNumber:   order.orderNumber || order._id?.slice(-8) || 'N/A',
          amount:        order.orderPayment?.totalAmount || 0,
          discount:      ReportService.extractDiscount(order),
          // Service charge lives in order.fees.serviceFeeAmount (confirmed from API response)
          serviceCharge: parseFloat(order.fees?.serviceFeeAmount || 0),
          subtotal:      order.fees?.baseAmountAfterPromotions || order.orderPayment?.totalAmount || 0,
          orderDate:     order.orderDate || order.createdAt,
          orderType:     order.orderType || 'N/A',
          customerName:  order.customer?.name || 'Walk-in',
          isReturn:      false
        }))

        // Returns: fetch ALL, filter by today + branch on frontend
        let returnRows = []
        try {
          const returnsResp = await ApiService.get('/order-returns')
          const allReturns  = returnsResp?.data?.orderReturns || returnsResp?.data?.returns || []

          returnRows = allReturns
            .filter(r =>
              ReportService.isToday(r.createdAt || r.returnDate) &&
              ReportService.returnMatchesBranch(r, branchId)
            )
            .map(r => {
              let returnItemsAmt = 0
              let returnedSC = 0
              let returnDateVal = r.createdAt || r.returnDate

              if (r.refundedServiceFee !== undefined && r.refundedServiceFee > 0) {
                returnedSC = parseFloat(r.refundedServiceFee)
                returnItemsAmt = parseFloat(r.totalAmount || 0) - returnedSC
              } else {
                const orderSC  = parseFloat(r.order?.fees?.serviceFeeAmount || 0)
                const baseAmt  = parseFloat(r.order?.fees?.baseAmountAfterPromotions || r.order?.orderPayment?.totalAmount || 0)
                const returnAmt = parseFloat(r.totalAmount || 0)
                
                returnedSC = baseAmt > 0 ? (returnAmt / baseAmt) * orderSC : 0
                returnItemsAmt = returnAmt
              }

              // Chronological check: If return time appears before order time, it must be in UTC.
              // Adjust return date by adding 5.5 hours timezone offset (GMT+5:30)
              const orderDateVal = new Date(r.order?.orderDate || r.order?.createdAt)
              if (returnDateVal && !isNaN(orderDateVal.getTime())) {
                const rDate = new Date(returnDateVal)
                if (rDate < orderDateVal) {
                  const adjusted = new Date(rDate.getTime() + 5.5 * 60 * 60 * 1000)
                  returnDateVal = adjusted.toISOString()
                }
              }

              const displayAmount = -(returnItemsAmt + returnedSC)
              return {
                orderNumber:  r.order?.orderNumber || r.orderNumber || r._id?.slice(-8) || 'N/A',
                amount:       displayAmount,
                discount:     0,
                subtotal:     0,
                orderDate:    returnDateVal,
                orderType:    'Return',
                customerName: r.order?.customer?.name || 'Walk-in',
                isReturn:     true,
                returnItems:  r.items || [],
                returnedSC,
                returnItemsAmt
              }
            })
        } catch (e) {
          console.warn('Could not fetch returns for sales report:', e.message)
        }

        const allRows         = [...salesRows, ...returnRows]
        const totalSales      = salesRows.reduce((s, o) => s + o.amount, 0)
        const totalDiscounts  = salesRows.reduce((s, o) => s + o.discount, 0)
        // totalReturns uses Math.abs(r.amount) which now includes the SC portion of the refund
        const totalReturns    = returnRows.reduce((s, r) => s + Math.abs(r.amount), 0)
        // netSales = Gross (includes SC) - Returns (includes refunded SC) → already true net
        const netSales        = totalSales - totalReturns
        // Service charge breakdown (informational) — SC is already inside totalSales/totalReturns
        const totalServiceCharge    = salesRows.reduce((s, o) => s + (o.serviceCharge || 0), 0)
        const returnedServiceCharge = returnRows.reduce((s, r) => s + (r.returnedSC || 0), 0)
        const netServiceCharge      = Math.max(0, totalServiceCharge - returnedServiceCharge)
        // grandTotal = netSales (SC already included; do NOT add SC again)
        const grandTotal = netSales

        return {
          success: true,
          data: {
            orders: allRows,
            totals: {
              totalSales,
              totalDiscounts,
              totalReturns,
              netSales,
              totalServiceCharge,
              returnedServiceCharge,
              netServiceCharge,
              grandTotal
            }
          }
        }
      }

      return { success: false, message: 'No sales data found' }
    } catch (error) {
      console.error('Error fetching sales report:', error)
      return { success: false, message: error.message }
    }
  }

  // ─── Returns Report ───────────────────────────────────────────────────────────
  // Backend has no branch param — fetch ALL, filter today + branch on frontend.
  static async getReturnsReport(branchId, date = null) {
    try {
      const { todayDateString } = ReportService.getTodayDateRange()
      const targetDate = date || todayDateString

      // Fetch ALL returns — no branch param sent (backend ignores it anyway)
      const response = await ApiService.get('/order-returns')
      const allReturns = response?.data?.orderReturns || response?.data?.returns || []

      // Frontend filtering: today + this branch
      const filtered = allReturns.filter(r => {
        const isToday   = ReportService.isToday(r.createdAt || r.returnDate)
        const isBranch  = ReportService.returnMatchesBranch(r, branchId)
        return isToday && isBranch
      })

      console.log(`📊 Returns report: ${filtered.length} of ${allReturns.length} match branch ${branchId} today`)

      const returns = filtered.map(r => ({
        orderNumber:   r.order?.orderNumber || r.orderNumber || r._id?.slice(-8) || 'N/A',
        returnId:      r._id || 'N/A',
        customerName:  r.order?.customer?.name || 'Walk-in',
        refundMethod:  r.refundMethod || 'cash',
        reasonType:    r.reasonType || '',
        returnAmount:  r.totalAmount || 0,
        numberOfItems: r.items?.reduce((s, i) => s + (i.returnQuantity || 1), 0) || 0,
        items:         r.items || [],
        returnDate:    r.createdAt || r.returnDate,
        handledBy:     r.createdBy?.username || r.handledBy?.fullName || 'N/A'
      }))

      const totalReturns = returns.reduce((s, r) => s + r.returnAmount, 0)
      const totalItems   = returns.reduce((s, r) => s + r.numberOfItems, 0)

      return {
        success: true,
        data: { returns, totals: { totalReturns, totalItems } }
      }
    } catch (error) {
      console.error('Error fetching returns report:', error)
      return { success: false, message: error.message }
    }
  }

  // ─── Stock Report ─────────────────────────────────────────────────────────────
  // Fetch all stock alerts; filter by branch on frontend as well.
  static async getStockReport(branchId) {
    try {
      const response = await ApiService.get(AppURLs.STOCK_ALERTS.GET_ALL, {
        branch: branchId   // send branch param — supported by stock alerts API
      })

      if (response.success && response.data) {
        let items = response.data.items || []

        // Frontend branch filter as safety net
        if (branchId) {
          items = items.filter(item => {
            const itemBranch = item.branch?._id || item.branch || item.branchId || ''
            return !itemBranch || String(itemBranch) === String(branchId)
          })
        }

        const summary = response.data.summary || {}

        const stockData = items.map(item => ({
          itemName:     item.name || 'Unknown Item',
          itemCode:     item.code || item.sku || 'N/A',
          type:         item.itemType === 'raw_material' ? 'Raw Material' : 'Product',
          currentStock: item.currentStock || 0,
          threshold:    item.threshold || 0,
          stockStatus:  item.stockStatus || 'unknown',
          unit:         item.unit?.abbreviation || item.unit?.name || 'units',
          category:     item.category?.name || 'N/A'
        }))

        // Recalculate summary from filtered items
        const outOfStock  = stockData.filter(i => i.stockStatus === 'out_of_stock').length
        const lowStock    = stockData.filter(i => i.stockStatus === 'low_stock').length
        const products    = stockData.filter(i => i.type === 'Product').length
        const rawMaterials = stockData.filter(i => i.type === 'Raw Material').length

        return {
          success: true,
          data: {
            items: stockData,
            summary: {
              totalItems:   stockData.length,
              outOfStock:   summary.outOfStock  ?? outOfStock,
              lowStock:     summary.lowStock    ?? lowStock,
              products:     summary.products    ?? products,
              rawMaterials: summary.rawMaterials ?? rawMaterials
            }
          }
        }
      }

      return {
        success: false,
        message: 'No stock data found',
        data: { items: [], summary: { totalItems: 0, outOfStock: 0, lowStock: 0, products: 0, rawMaterials: 0 } }
      }
    } catch (error) {
      return {
        success: false,
        message: error.message,
        data: { items: [], summary: { totalItems: 0, outOfStock: 0, lowStock: 0, products: 0, rawMaterials: 0 } }
      }
    }
  }

  // ─── Daily Summary / Audit Report ────────────────────────────────────────────
  // Combines sales + returns + product breakdown + payment breakdown in one call.
  static async getDailySummaryReport(branchId, date = null) {
    try {
      const { todayDateString } = ReportService.getTodayDateRange()
      const targetDate = date || todayDateString

      // ── Orders ──────────────────────────────────────────────────────────────
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, {
        branch: branchId,
        startDate: targetDate,
        endDate: targetDate,
        dateFrom: targetDate,
        dateTo: targetDate,
        orderStatus: 'completed',
        paymentStatus: 'paid'
      })

      let orders = []
      if (response.success && response.data?.orders) {
        orders = response.data.orders.filter(o => {
          const ds = new Date(o.orderDate || o.createdAt).toISOString().split('T')[0]
          return ds === targetDate && o.orderStatus === 'completed' && o.paymentStatus === 'paid'
        })
      }

      // ── Returns ─────────────────────────────────────────────────────────────
      let returns = []
      try {
        const returnsResp = await ApiService.get('/order-returns')
        const allReturns  = returnsResp?.data?.orderReturns || returnsResp?.data?.returns || []
        returns = allReturns.filter(r =>
          ReportService.isToday(r.createdAt || r.returnDate) &&
          ReportService.returnMatchesBranch(r, branchId)
        )
      } catch (e) {
        console.warn('Daily summary: could not fetch returns', e.message)
      }

      // ── Aggregate sales ──────────────────────────────────────────────────────
      let grossSales     = 0
      let totalDiscounts = 0
      let totalServiceCharge = 0
      let totalPackageFee    = 0
      const paymentMap  = {}   // method → amount
      const productMap  = {}   // productId → { name, qty, revenue }

      orders.forEach(order => {
        const amount      = parseFloat(order.orderPayment?.totalAmount || 0)
        const discount    = ReportService.extractDiscount(order)
        const sc          = parseFloat(order.fees?.serviceFeeAmount  || 0)
        const pkg         = parseFloat(order.fees?.packageFeeAmount  || 0)

        grossSales        += amount
        totalDiscounts    += discount
        totalServiceCharge += sc
        totalPackageFee   += pkg

        // Payment breakdown
        ;(order.orderPayment?.paymentMethods || []).forEach(pm => {
          const method = (pm.method || 'cash').toLowerCase()
          const label  = method === 'card'   ? 'Card'
                       : method === 'cash'   ? 'Cash'
                       : method === 'redeem' ? 'Points Redeem'
                       : method.charAt(0).toUpperCase() + method.slice(1)
          paymentMap[label] = (paymentMap[label] || 0) + parseFloat(pm.amount || 0)
        })

        // Product breakdown
        ;(order.orderItems || []).forEach(item => {
          const product   = item.product || {}
          const pid       = product._id || item.productId || item.product || `unknown`
          const name      = product.name || item.name || item.productName || 'Unknown'
          const qty       = parseFloat(item.quantity || 0)
          const lineTotal = parseFloat(
            item.promotionEngineResult?.totals?.finalPrice ?? (qty * parseFloat(item.unitPrice || 0))
          )
          if (!productMap[pid]) productMap[pid] = { name, qty: 0, revenue: 0 }
          productMap[pid].qty     += qty
          productMap[pid].revenue += lineTotal
        })
      })

      // ── Aggregate returns ────────────────────────────────────────────────────
      let totalRefunds = 0
      returns.forEach(r => { totalRefunds += parseFloat(r.totalAmount || 0) })

      const netSales    = grossSales - totalRefunds
      const grandTotal  = netSales   // SC already inside grossSales

      // Sort products by qty desc
      const products = Object.values(productMap).sort((a, b) => b.qty - a.qty)

      return {
        success: true,
        data: {
          summary: {
            grossSales,
            totalDiscounts,
            totalRefunds,
            netSales,
            totalServiceCharge,
            totalPackageFee,
            grandTotal,
            totalOrders: orders.length,
            totalReturns: returns.length
          },
          paymentBreakdown: paymentMap,
          products,
          date: targetDate
        }
      }
    } catch (error) {
      console.error('Error fetching daily summary report:', error)
      return { success: false, message: error.message }
    }
  }

  // ─── Branch details ───────────────────────────────────────────────────────────
  static async getBranchDetails(branchId) {
    try {
      const endpoint = ApiService.replaceUrlParams(AppURLs.BRANCHES.GET_BY_ID, { id: branchId })
      const response = await ApiService.get(endpoint)
      return response.success ? response.data : null
    } catch (error) {
      console.error('Error fetching branch details:', error)
      return null
    }
  }
}

export default ReportService